/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { LucideLoaderCircle } from '@lucide/angular';
import { IconComponent } from '../icon/icon.component';

/**
 * A refresh in progress over content that is still on screen — the counterpart to a skeleton, which is for
 * content that is not there yet. The veil breathes rather than spins, so several of them across a page read
 * as one thing updating; the spinner is for the piece the reader is actually looking at (a table, a list),
 * and says what the quieter veils elsewhere mean.
 *
 * The host is absolutely positioned, so **the container must establish a positioning context**
 * (`position: relative`). While active it also swallows pointer events: the values underneath are about to
 * be replaced, so acting on them is acting on stale data.
 */
@Component({
  selector: 'ptl-busy-overlay',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  template: `
    @if (active()) {
      <span class="busy__veil" aria-hidden="true"></span>
      @if (spinner()) {
        <span class="busy__spinner" role="status" [attr.aria-label]="label() || null">
          <ptl-icon [icon]="LucideLoaderCircle" [size]="'l'" [color]="'currentColor'"></ptl-icon>
        </span>
      }
    }
  `,
  styles: [
    `
      @use 'rem' as r;
      @use 'v3-theme-colors' as c;

      :host {
        position: absolute;
        inset: 0;
        z-index: 4;
        display: flex;
        justify-content: center;
        pointer-events: none;
        /* Resolves to the container's radius, so the veil stops at a card's corner rather than over it. */
        border-radius: inherit;
      }

      :host:empty {
        display: none;
      }

      .busy__veil {
        position: absolute;
        inset: 0;
        background: c.$neutral-white;
        border-radius: inherit;
        pointer-events: auto;
        animation: ptl-busy-breathe 1.6s ease-in-out infinite;
      }

      /*
       * Sticky, so on a table taller than the window the spinner follows the reader down it instead of
       * sitting at a midpoint they may never scroll to. It only engages once the overlay's top edge has
       * passed the offset; on a short block it simply rests near the top.
       */
      .busy__spinner {
        position: sticky;
        top: r.rem(140);
        align-self: flex-start;
        margin-block-start: r.rem(28);
        display: flex;
        height: r.rem(24);
        color: c.$surface-action;
        animation: ptl-busy-spin 0.9s linear infinite;
      }

      @keyframes ptl-busy-breathe {
        0%,
        100% {
          opacity: 0.3;
        }

        50% {
          opacity: 0.72;
        }
      }

      @keyframes ptl-busy-spin {
        from {
          transform: rotate(0deg);
        }

        to {
          transform: rotate(360deg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .busy__veil {
          animation: none;
          opacity: 0.55;
        }

        .busy__spinner {
          animation: none;
        }
      }
    `,
  ],
})
export class PtlBusyOverlayComponent {
  /** Whether a refresh is in flight. Nothing is rendered while false. */
  readonly active = input(false);
  /** Adds the spinner. For the one piece of the page the reader is watching — not for every veiled block. */
  readonly spinner = input(false);
  /** Announced by the spinner's live region, e.g. "Updating results". */
  readonly label = input('');

  protected readonly LucideLoaderCircle = LucideLoaderCircle;
}
